import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import Textarea from "../../../ui/Textarea";
import FileUploadNotes from "./FileUploadNotes";
import SelectFamily from "../../../ui/SelectFamily";
import { ViewNoteFiles } from "./ViewNoteFiles";
import { fetchNoteDocuments } from "../../../../services/httpClient";
import { useParams } from "react-router-dom";
import ViewRecordings from "./ViewRecordings";
import DatePicker from "../../../ui/DatePicker";
import { User } from "../../../../types/user";
import { Note } from "../../../../types/note";

interface StandardState {
  note?: Note;
  family: User[];
  documents: any;
  setDocuments: any;
  updatedNote?: any;
  setUpdatedNote: any;
  setSaveIsEnabled: any;
  setUserToSave: any;
  myCase?: boolean;
  editNote?: boolean;
  setEditNote?: any;
  setCreateNote?: any;
  setCurrentNote?: any;
  setShowToast?: any;
  setToastText?: any;
  setToastBody?: any;
}

const Standard = ({
  note,
  family,
  documents,
  setDocuments,
  updatedNote,
  setUpdatedNote,
  setSaveIsEnabled,
  setUserToSave,
  myCase,
  editNote = false,
  setEditNote,
  setCreateNote,
  setCurrentNote,
  setShowToast,
  setToastText,
  setToastBody,
}: StandardState) => {
  const { id } = useParams();
  const mainRef = useRef();
  const [familyUser, setFamilyUser] = useState<string>("unassigned");
  const [text, setText] = useState<string>("");
  const [noteDate, setNoteDate] = useState<string>("");
  const [errMsg, setErrMsg] = useState("");
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialReadOnly, setInitialReadOnly] = useState<boolean>(true);

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    handleScroll(mainRef);

    const realFamily = family.filter((member) => member.name !== "Unassigned");

    if (editNote) {
      if (note?.subject) {
        setFamilyUser(note?.subject?.id + "|" + note?.subject?.type);

        const isDeleted = family.find((f) => f.id == note?.subject?.id)?.isDeleted;
        if (isDeleted === undefined) {
          setInitialReadOnly(!myCase);
        } else {
          setInitialReadOnly(!myCase || isDeleted);
        }
      }
      setNoteDate(note?.fields?.date);
      setText(note?.fields?.text);
      setShowUpload(true);
      getNoteDocuments();
    } else {
      setNoteDate(new Date().toISOString().split("T")[0]);
      setInitialReadOnly(!myCase);
    }

    if (realFamily.length === 1 && !editNote) {
      setFamilyUser(realFamily[0].id + "|" + realFamily[0].type);
    }
  }, []);

  useEffect(() => {
    const parts = familyUser.split("|");

    const note = {
      notetype: "standard",
      subject:
        familyUser !== "unassigned"
          ? {
              id: parts[0],
              type: parts[1],
            }
          : { id: 0 },
      fields: {
        text: text,
        date: noteDate,
      },
    };

    setUpdatedNote(note);
  }, [text, noteDate, familyUser, setUpdatedNote]);

  const getNoteDocuments = () => {
    setIsLoading(true);
    fetchNoteDocuments(id, note?.id).then((res: any) => {
      setDocuments(res[0].data);
      setIsLoading(false);
    });
  };

  const updateFamilyUser = (e: any) => {
    setFamilyUser(e.target.value);

    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateText = (e: any) => {
    setText(e.target.value);

    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateDate = () => {
    setSaveIsEnabled(true);
    setUserToSave(true);
  };

  useEffect(() => {
    if (!noteDate) {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  }, [noteDate]);

  return (
    <Stack gap={3} className="mt-4">
      <p className={errMsg ? "errmsg mt-3" : "offscreen"} aria-live="assertive">
        {errMsg}
      </p>
      <Row ref={mainRef}>
        <SelectFamily
          family={family}
          familyUser={familyUser ? familyUser : "unassigned"}
          updateFamilyUser={updateFamilyUser}
          readOnly={initialReadOnly}
          defaultSelection="Unassigned"
        />
        <Col className="col-lg-4 col-md-6 col-sm-8">
          <DatePicker
            label="Date"
            currentValue={noteDate}
            setCurrentValue={setNoteDate}
            onUpdate={() => updateDate()}
            required={true}
            hasMaxDate={true}
            disabled={myCase ? false : true}
          />
        </Col>
      </Row>
      {note?.recordingsExist === true && <ViewRecordings note={note} editNote={editNote} myCase={myCase} />}
      <Row>
        <Col xs={12}>
          <Textarea label="Notes" value={text} onUpdate={(e: any) => updateText(e)} disabled={myCase ? false : true} />
        </Col>
      </Row>
      <Row>
        <>
          <h5 className="app-header-text">Documents</h5>
          {isLoading ? (
            <Col className="col-lg-8 col-sm-12 col-12">
              <Container className={"lg div-main"}>
                <Button size="sm" className="spinner-button spinner-button-main">
                  <Spinner animation="border" style={{ color: "#F6893D" }} />
                </Button>
              </Container>
            </Col>
          ) : (
            <Col className="col-lg-8 col-sm-12 col-12">
              <ViewNoteFiles
                documents={documents}
                setDocuments={setDocuments}
                note={note}
                editNote={editNote}
                myCase={myCase}
                setIsLoading={setIsLoading}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
              />
            </Col>
          )}
          {myCase && (
            <Col className="col-lg-4 col-sm-12 col-12">
              <FileUploadNotes
                header="Upload Document"
                documents={documents}
                setDocuments={setDocuments}
                note={note}
                editNote={editNote}
                getDocuments={getNoteDocuments}
                setErrorMessage={setErrMsg}
                setIsLoading={setIsLoading}
              />
            </Col>
          )}
        </>
      </Row>
    </Stack>
  );
};

export default Standard;
